import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  UserPlus, Pencil, Trash2, Mail, Phone, Building2, Send, X, CheckCircle2, Plus, Search, Copy, Check,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { LEGACY_ROLE_LABELS } from "../lib/constants";

// Fallback role list used when settings haven't loaded yet
const FALLBACK_ROLES = [
  "Client", "Client Representative", "Architectural Design",
  "Contractor", "Structural Engineer", "Planning Consultant",
  "Quantity Surveyor", "M&E Consultant", "Supplier", "Sub-Contractor", "Other",
];

// Display a contact's role — handles both new display-string values and
// legacy snake_case values stored before user-configurable roles were added.
const roleLabel = (c) => {
  if (c.role === "other" || c.role === "Other") return c.role_custom?.trim() || "Other";
  return LEGACY_ROLE_LABELS[c.role] || c.role;
};

/**
 * ClientProjectTeam — per-project contacts directory. Replaces the old
 * "Profile" sub-page. Admin can manage contacts; clients see read-only.
 *
 * Used by both:
 *  - /portal/projects/{id}/team (client)  → reads project from outletContext
 *  - Admin Project Detail "Team" tab     → receives project + reload as props
 */
export default function ClientProjectTeam({ project: projectProp, reload: reloadProp } = {}) {
  const ctx = useOutletContext();
  const project = projectProp || ctx?.project;
  const reload = reloadProp || ctx?.reload;
  const { user } = useAuth();
  const { settings } = useSettings();
  const isAdmin = user?.role === "admin";
  const roleOptions = settings?.contact_roles?.length ? settings.contact_roles : FALLBACK_ROLES;

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { name, email, activation_url, emailSent }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/contacts`);
      setContacts(data);
    } catch (_e) {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const onUnassign = async (contactId) => {
    if (!confirm("Remove this contact from the project? They stay in your global directory.")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}/contacts/${contactId}`);
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onInvite = async (contact) => {
    if (!contact.email) {
      alert("Add an email address before inviting this contact to the portal.");
      return;
    }
    if (!confirm(`Invite ${contact.name} to the portal? They'll get login access to ${project.project_name}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/invite`, { project_id: project.id, permission: "view_only" });
      await load();
      reload?.();
      // Show inline activation link so admin can copy it as a fallback
      setInviteResult({
        name: contact.name,
        email: contact.email,
        activation_url: data.activation_url,
        emailSent: data.email?.status === "sent",
      });
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="client-project-team">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">Project team</div>
          <h2 className="font-display text-3xl text-[color:var(--atech-text)]">Team & contacts</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-1.5 max-w-xl">
            Everyone connected to this project — client representatives, consultants,
            and contractors. {isAdmin && "Add contacts as the team grows."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            className="btn-copper text-sm flex items-center gap-2"
            data-testid="team-add-contact-button"
          >
            <UserPlus size={14} /> Add contact
          </button>
        )}
      </div>

      {loading ? (
        <div className="label text-[color:var(--atech-text-muted)]">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="team-empty-state">
          <div className="text-sm text-[color:var(--atech-text)]">
            No team members on this project yet.
          </div>
          <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
            {isAdmin
              ? "Add the client, contractor, structural engineer, and any consultants as the project takes shape."
              : "A-Tech will list project contacts here as the team is assembled."}
          </p>
          {isAdmin && (
            <button
              onClick={() => setShowAdd(true)}
              className="mt-4 text-xs text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)] inline-flex items-center gap-1.5"
              data-testid="team-empty-add-button"
            >
              <Plus size={13} /> Add first contact
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              isAdmin={isAdmin}
              busy={busy}
              onEdit={() => setEditing(c)}
              onUnassign={() => onUnassign(c.id)}
              onInvite={() => onInvite(c)}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddContactModal
          projectId={project.id}
          existingIds={contacts.map((c) => c.id)}
          roleOptions={roleOptions}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <EditContactModal
          contact={editing}
          roleOptions={roleOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {inviteResult && (
        <InviteResultModal info={inviteResult} onClose={() => setInviteResult(null)} />
      )}
    </div>
  );
}

/* ─── Contact card ──────────────────────────────────────────────────────── */

function ContactCard({ contact, isAdmin, busy, onEdit, onUnassign, onInvite }) {
  const linked = !!contact.linked_user_id;
  return (
    <div className="card-surface p-5 hover-lift" data-testid={`team-contact-${contact.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
            style={{ color: "var(--atech-copper)" }}
          >
            {roleLabel(contact)}
          </div>
          <h3 className="font-display text-lg text-[color:var(--atech-text)] truncate">
            {contact.name}
          </h3>
          {contact.company && contact.company !== contact.name && (
            <div className="flex items-center gap-1.5 text-xs text-[color:var(--atech-text-dim)] mt-1">
              <Building2 size={11} className="shrink-0" />
              <span className="truncate">{contact.company}</span>
            </div>
          )}
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-[2px] text-[9px] uppercase tracking-[0.16em] border"
          style={
            linked
              ? {
                  color: "var(--st-complete-fg)",
                  backgroundColor: "var(--st-complete-bg)",
                  borderColor: "var(--st-complete-bd)",
                }
              : {
                  color: "var(--atech-text-muted)",
                  backgroundColor: "var(--st-pending-bg)",
                  borderColor: "var(--st-pending-bd)",
                }
          }
          data-testid={`team-contact-portal-status-${contact.id}`}
          title={linked ? "Has portal access" : "Contact only — no portal login"}
        >
          {linked ? <CheckCircle2 size={9} /> : <X size={9} />}
          {linked ? "Portal" : "Contact"}
        </span>
      </div>

      <dl className="mt-4 space-y-1.5 text-xs">
        {contact.email && (
          <div className="flex items-center gap-2 text-[color:var(--atech-text-dim)]">
            <Mail size={11} className="shrink-0" />
            <a
              href={`mailto:${contact.email}`}
              className="truncate hover:text-[color:var(--atech-copper)] transition-colors"
            >
              {contact.email}
            </a>
          </div>
        )}
        {contact.phone && (
          <div className="flex items-center gap-2 text-[color:var(--atech-text-dim)]">
            <Phone size={11} className="shrink-0" />
            <a
              href={`tel:${contact.phone}`}
              className="hover:text-[color:var(--atech-copper)] transition-colors"
            >
              {contact.phone}
            </a>
          </div>
        )}
        {contact.notes && (
          <div className="text-[color:var(--atech-text-muted)] mt-2 pt-2 border-t border-[color:var(--atech-border)] leading-relaxed">
            {contact.notes}
          </div>
        )}
      </dl>

      {isAdmin && (
        <div className="mt-4 pt-4 border-t border-[color:var(--atech-border)] flex items-center gap-2">
          {!linked && contact.email && (
            <button
              onClick={onInvite}
              disabled={busy}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-[2px] border border-[color:var(--atech-copper)] text-[color:var(--atech-copper)] hover:bg-[color:var(--atech-copper)] hover:text-white transition-colors disabled:opacity-50"
              data-testid={`team-invite-${contact.id}`}
            >
              <Send size={11} /> Invite to portal
            </button>
          )}
          <button
            onClick={onEdit}
            className="text-xs flex items-center gap-1.5 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] ml-auto"
            data-testid={`team-edit-${contact.id}`}
          >
            <Pencil size={11} /> Edit
          </button>
          <button
            onClick={onUnassign}
            disabled={busy}
            className="text-xs flex items-center gap-1.5 text-[color:var(--atech-text-dim)] hover:text-[#F87171] disabled:opacity-50"
            data-testid={`team-remove-${contact.id}`}
          >
            <Trash2 size={11} /> Remove
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Add contact modal — pick existing or create new ───────────────────── */

function AddContactModal({ projectId, existingIds, roleOptions, onClose, onAdded }) {
  const [mode, setMode] = useState("pick"); // "pick" | "new"
  const [directory, setDirectory] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/contacts").then(({ data }) => setDirectory(data)).catch(() => setDirectory([]));
  }, []);

  const available = useMemo(() => {
    const set = new Set(existingIds);
    const q = search.trim().toLowerCase();
    return directory
      .filter((c) => !set.has(c.id))
      .filter((c) => !q
        || (c.name || "").toLowerCase().includes(q)
        || (c.company || "").toLowerCase().includes(q)
        || (c.email || "").toLowerCase().includes(q));
  }, [directory, existingIds, search]);

  const assignExisting = async (id) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/projects/${projectId}/contacts/${id}`);
      onAdded();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const createAndAssign = async (form) => {
    setBusy(true); setErr("");
    try {
      const { data: created } = await api.post("/contacts", form);
      await api.post(`/projects/${projectId}/contacts/${created.id}`);
      onAdded();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add contact" wide>
      <div className="flex border border-[color:var(--atech-border)] rounded-[2px] overflow-hidden mb-5">
        <button
          onClick={() => setMode("pick")}
          className={`flex-1 text-xs py-2 transition-colors ${mode === "pick" ? "bg-[color:var(--atech-copper)] text-white" : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"}`}
          data-testid="team-add-mode-pick"
        >
          Pick from directory ({directory.length})
        </button>
        <button
          onClick={() => setMode("new")}
          className={`flex-1 text-xs py-2 transition-colors border-l border-[color:var(--atech-border)] ${mode === "new" ? "bg-[color:var(--atech-copper)] text-white" : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"}`}
          data-testid="team-add-mode-new"
        >
          New contact
        </button>
      </div>

      {err && (
        <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2 mb-3">
          {err}
        </div>
      )}

      {mode === "pick" ? (
        <PickFromDirectory
          available={available}
          search={search}
          setSearch={setSearch}
          onPick={assignExisting}
          busy={busy}
        />
      ) : (
        <NewContactForm onSubmit={createAndAssign} busy={busy} roleOptions={roleOptions} />
      )}
    </Modal>
  );
}

function PickFromDirectory({ available, search, setSearch, onPick, busy }) {
  return (
    <div data-testid="team-pick-directory">
      <div className="flex items-center gap-2 border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2 mb-3">
        <Search size={14} className="text-[color:var(--atech-text-muted)]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, company, email…"
          className="bg-transparent border-none focus:outline-none text-sm flex-1 placeholder:text-[color:var(--atech-text-muted)]"
          data-testid="team-pick-search"
        />
      </div>
      {available.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-sm text-[color:var(--atech-text)]">
            Nothing matches.
          </div>
          <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5">
            Switch to <span className="text-[color:var(--atech-copper)]">New contact</span> to create one from scratch.
          </p>
        </div>
      ) : (
        <ul className="space-y-1 max-h-[360px] overflow-y-auto">
          {available.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => onPick(c.id)}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 hover:bg-[color:var(--atech-surface-hover)] rounded-[2px] transition-colors disabled:opacity-50 group"
                data-testid={`team-pick-${c.id}`}
              >
                <div className="text-sm text-[color:var(--atech-text)] group-hover:text-[color:var(--atech-copper)]">
                  {c.name}
                </div>
                <div className="text-[11px] text-[color:var(--atech-text-muted)] mt-0.5 flex flex-wrap gap-x-2">
                  <span>{roleLabel(c)}</span>
                  {c.company && <span>· {c.company}</span>}
                  {c.email && <span>· {c.email}</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewContactForm({ onSubmit, busy, initial = {}, roleOptions = [] }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    role: initial.role || (roleOptions[0] || "Client"),
    role_custom: initial.role_custom || "",
    company: initial.company || "",
    email: initial.email || "",
    phone: initial.phone || "",
    notes: initial.notes || "",
  });
  const isOther = form.role === "other" || form.role === "Other";
  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, role_custom: isOther ? form.role_custom : "" });
  };
  const cls = "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="team-new-contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name *">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} data-testid="team-new-name" />
        </Field>
        <Field label="Role">
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={cls} data-testid="team-new-role">
            {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      </div>
      {isOther && (
        <Field label="Custom role *">
          <input required value={form.role_custom} onChange={(e) => setForm({ ...form, role_custom: e.target.value })} placeholder="e.g. Acoustic consultant" className={cls} data-testid="team-new-role-custom" />
        </Field>
      )}
      <Field label="Company">
        <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={cls} data-testid="team-new-company" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} data-testid="team-new-email" />
        </Field>
        <Field label="Phone">
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cls} data-testid="team-new-phone" />
        </Field>
      </div>
      <Field label="Notes">
        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${cls} resize-none`} />
      </Field>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="team-new-submit">
          {busy ? "Saving…" : "Add to project"}
        </button>
      </div>
    </form>
  );
}

function EditContactModal({ contact, roleOptions, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (form) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/contacts/${contact.id}`, form);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Edit ${contact.name}`} wide>
      {err && (
        <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2 mb-3">
          {err}
        </div>
      )}
      <NewContactForm onSubmit={save} busy={busy} initial={contact} roleOptions={roleOptions} />
    </Modal>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="label block mb-2">{label}</span>
    {children}
  </label>
);

function InviteResultModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(info.activation_url); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch { /* clipboard unavailable */ }
  };
  return (
    <Modal onClose={onClose} title="Contact invited">
      <div className="space-y-4" data-testid="invite-result-modal">
        {info.emailSent ? (
          <div className="flex items-center gap-2 text-[#34D399] text-sm" data-testid="invite-email-sent-msg">
            <CheckCircle2 size={14} /> Invite email sent to <span className="font-mono">{info.email}</span>
          </div>
        ) : (
          <div className="text-sm text-[color:var(--atech-text-dim)]">
            Email provider not configured — share the activation link below manually.
          </div>
        )}
        {info.activation_url && (
          <div>
            <div className="label mb-2">Activation link for {info.name}</div>
            <div className="flex items-center gap-2 bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] p-2.5 overflow-hidden">
              <input readOnly value={info.activation_url} className="flex-1 bg-transparent outline-none text-[color:var(--atech-text)] font-mono text-[10px] truncate" data-testid="invite-link-field" />
              <button onClick={copy} className="text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)] flex items-center gap-1 text-xs shrink-0" data-testid="copy-invite-link">
                {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-copper text-sm" data-testid="invite-result-close">Done</button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`card-surface w-full ${wide ? "max-w-2xl" : "max-w-md"} p-8 fade-up max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-[color:var(--atech-text)]">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]" data-testid="modal-close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
